import StationDataHandler from "./StationDataHandler";
import TiledPoiLayer from "./TiledPoiLayer";
import overlays from "../../app/overlays";
import layers from "../../app/layers";
import trans from "../../app/trans";
import format from "../../app/format";
import rootScope from "../../app/rootScope";
import store from "../../app/store";

/**
 * Clase que maneja los datos de calidad del aire (AQI).
 * Extiende la funcionalidad de `StationDataHandler` para gestionar estaciones de calidad del aire.
 */
class AqiDataHandler extends StationDataHandler {
    /**
     * Inicializa las propiedades específicas del controlador de AQI.
     */
    initProperties() {
        this.ident = "aqi";
        super.initProperties();
        const aqindex = store.get("aqioverlay");
        this.setAqiIdent(aqindex);
        this.layer = new TiledPoiLayer(this,this.generalOptions);
    }
    /**
     * Configura el identificador de AQI y actualiza las propiedades relacionadas.
     *
     * @param {string} ovrIdent - El identificador del overlay de AQI.
     */
    setAqiIdent(ovrIdent) {
        this.ovrIdent = ovrIdent;
        this.overlay = overlays[ovrIdent];
        this.displayItem = (o, t) => null !== o[ovrIdent][t];
    }
    /**
     * Actualiza un elemento HTML con los datos de AQI.
     *
     * @param {HTMLElement} element - El elemento HTML que se actualizará.
     * @param {Object} aqiData - Los datos de AQI.
     * @param {number} index - El índice del contaminante.
     */
    updateElement(element, aqiData, index) {
        if (this.ovrIdent === "aqieu")
            element.classList.add("aqieu");
        const aqi = aqiData[this.ovrIdent][index];
        this.updateElementFromNumber(element, aqi);
    }
    /**
     * Convierte los datos meteorológicos de un POI a una cadena HTML.
     *
     * @param {Object} poiData - Los datos del POI.
     * @returns {string} La cadena HTML generada.
     */
    wx2string2(poiData) {
        let html = "";

        const aqindex = this.ovrIdent;

        if (typeof poiData[aqindex].index === "number" && !isNaN(poiData[aqindex].index)) {
            const n = layers[aqindex].m.convertValue.call(layers[aqindex].m, poiData[aqindex].index, " ") //, */ n = poiData.aqimet.index
                , r = layers[aqindex].c.colorDark(poiData[aqindex].index, 20);
            html += `<span class="rounded-box" style="background: ${r}">${n}</span>`;
        }

        return html;
    }
    /**
     * Obtiene la URL de las teselas para las estaciones de AQI.
     *
     * @returns {string} La URL de las teselas.
     */
    getTilesUrl() {

         // return "http://localhost:8081/cgi-bin/otf_stations?type=aq&z={z}&x={x}&y={y}";
        // return "https://test-services.meteored.com/cgi-bin/otf_stations?type=aq&z={z}&x={x}&y={y}";
        return `${rootScope.poisURL}aq/{z}/{x}/{y}/`;
    }
    /**
     * Prepara los datos de AQI para su procesamiento.
     *
     * @param {Object} data - Los datos de AQI.
     */
    prepareData(data) {
        const key = this.type2type[data.type];
        let msg = trans["AQSTATION"];

        if (!msg)
            msg = trans["LASTUPDATE"]

        if (data.source !== null && data.source !== undefined){
            const src = trans["SOURCE"];
            msg = `${src}: <a href="${data.url}">${data.source}</a>`;
        }

        const aqindex = store.get("aqioverlay");

        data.msg = `${msg}, ${format.howOld({
            ts: data.ts,
            translate: true
        })}`;


        const polluntantsabr = {"NO2": "NO<sub>2</sub>", "O3" : "O<sub>3</sub>", "PM10": "PM<sub>10</sub>", "PM2P5": "PM<sub>2.5</sub>", "SO2": "SO<sub>2</sub>","CO": "CO"};


        if (data[aqindex] !== null && data[aqindex] !== undefined) {
            let value = layers[aqindex].m.convertValue.call(layers[aqindex].m, data[aqindex].index, " ");
            if (aqindex === "aqimet" || aqindex === "aqiusa")
                value = `${data[aqindex].index} - ${value}`
            value = `${value} (${polluntantsabr[data[aqindex].dominant=== "PM2.5" ? "PM2P5" : data[aqindex].dominant]})`;

            data.aqindex = {
                title: trans["AQI"],
                value: value,
                color: layers[aqindex].c.colorDark(data[aqindex].index, 20),
                icon: `${rootScope.resourcesServer}css/svgs/${overlays[aqindex].icon}`,
                class: "aqi"
            }
        }


        let index = 0;
        let npollutants = 0;

        Object.keys(polluntantsabr).forEach((pollutant) => {
            pollutant = pollutant.toLowerCase();
            if (data[pollutant] !== null && data[pollutant] !== undefined) {

                const aqi = data[aqindex][pollutant];
                const title = polluntantsabr[pollutant.toUpperCase()];

                data[pollutant]  = {
                    title: title,
                    value: layers[pollutant].m.convertValue.call(layers[pollutant].m, data[pollutant], " "),
                    color: layers[aqindex].c.colorDark(aqi, 20),
                    icon: `${rootScope.resourcesServer}css/svgs/${overlays[pollutant].icon}`

                }
                npollutants++;
            }
            index++;
        });


        if (npollutants >= 3){
            Object.keys(polluntantsabr).forEach((pollutant) => {
                pollutant = pollutant.toLowerCase();
                if (data[pollutant] !== null && data[pollutant] !== undefined) {
                    data[pollutant].class = "column3";
                }
            });
        }

        if (npollutants === 0)
            data.ncolumns = 1;
        else if (npollutants <= 3)
            data.ncolumns = 2;
        else
            data.ncolumns = 3;

        return data;

    }

    /**
     * Redibuja la capa de AQI si se fuerza el redibujado.
     *
     * @param {boolean} forceRedraw - Indica si se debe forzar el redibujado de la capa.
     */
    redraw(forceRedraw) {
        const aqindex = store.get("aqioverlay");
        if (this.layer && aqindex !== this.ovrIdent) {
            this.setAqiIdent(aqindex);
            this.layer._reset();
        }
    }


}

export default AqiDataHandler;
